import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../auth/supabase';
import TenantBrandLockup from './TenantBrandLockup';
import { authenticatedTenantBrand } from './tenantBrand';
import './authenticated-tenant-banner.css';

const GENERIC_PATH_PATTERNS = [
  /^\/app-review-demo(?:\/|$)/i,
  /^\/privacy(?:\/|$)/i,
  /^\/support(?:\/|$)/i,
  /^\/availability(?:\/|$)/i,
  /^\/staffing(?:\/|$)/i,
  /^\/drugcalc(?:\/|$)/i,
  /^\/(?:gear|asset)\//i,
  /^\/account(?:\/|$)/i,
];

function normalizedPath() {
  if (typeof window === 'undefined') return '/';
  return String(window.location?.pathname || '/').replace(/\/+$/, '') || '/';
}

function routeAllowsTenantBanner(path) {
  if (GENERIC_PATH_PATTERNS.some(pattern => pattern.test(path))) return false;
  // These two screens already render the tenant lockup inside their own header.
  if (path === '/admin' || path === '/settings') return false;
  return true;
}

export default function AuthenticatedTenantBanner() {
  const [email, setEmail] = useState('');
  const [path, setPath] = useState(normalizedPath);

  useEffect(() => {
    let mounted = true;

    const setFromSession = session => {
      if (!mounted) return;
      setEmail(String(session?.user?.email || '').trim().toLowerCase());
    };

    supabase.auth.getSession()
      .then(({ data }) => setFromSession(data?.session || null))
      .catch(() => setFromSession(null));

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setFromSession(session || null);
    });

    const syncRoute = () => setPath(normalizedPath());
    window.addEventListener('popstate', syncRoute);
    window.addEventListener('smfd:native-route', syncRoute);

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
      window.removeEventListener('popstate', syncRoute);
      window.removeEventListener('smfd:native-route', syncRoute);
    };
  }, []);

  const brand = useMemo(() => authenticatedTenantBrand({ email }), [email]);
  const showBanner = Boolean(email && brand.coBranded && routeAllowsTenantBanner(path));

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    root.classList.toggle('has-tenant-authenticated-banner', showBanner);
    return () => root.classList.remove('has-tenant-authenticated-banner');
  }, [showBanner]);

  if (!showBanner) return null;

  return (
    <div className="tenant-authenticated-banner" role="banner" aria-label={`${brand.displayName} authenticated program`}>
      <div className="tenant-authenticated-banner__inner">
        <TenantBrandLockup profile={{ email }} compact />
        <div className="tenant-authenticated-banner__agency">
          <span>Authenticated program</span>
          <strong>{brand.tenantName}</strong>
        </div>
      </div>
    </div>
  );
}
